/**
 * ShopPage — public Facebook-marketplace-style grid of all marketplace items.
 *
 * Design intent (per user brief):
 *   • Grid of item cards. Each card has a bold headline, large image, and a
 *     *glowing purple shadow* that makes the thumbnail stand out.
 *   • Item name on top of card, vendor name underneath.
 *   • Clicking the card goes to /shop/item/:itemId (purchase flow).
 *   • Clicking the vendor name goes to /vendor/:vendorId (profile + more
 *     items from the same seller).
 *   • Category chips scroll horizontally at the top so shoppers can filter
 *     between vendors by category.
 *   • Search box at the top, sort pill on the right.
 */
import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, Loader2, Store } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import useSeo from '@/hooks/useSeo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function ItemCard({ item }) {
  const img = (item.images && item.images[0])
    || 'data:image/svg+xml,%3Csvg xmlns=\'http://www.w3.org/2000/svg\' viewBox=\'0 0 400 400\'%3E%3Crect fill=\'%23312E81\' width=\'400\' height=\'400\'/%3E%3C/svg%3E';
  const price = Number(item.base_price || 0).toFixed(2);
  const compareAt = item.compare_at_price ? Number(item.compare_at_price).toFixed(2) : null;
  const outOfStock = item.in_stock === false;
  const imgSrc = img.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${img}` : img;

  return (
    <div
      className="group relative"
      data-testid={`shop-item-${item.item_id}`}
    >
      <Link
        to={`/shop/item/${item.item_id}`}
        className="block relative"
      >
        {/* The signature purple glow. Sits under the image so it reads
            as a cast shadow rather than a border. */}
        <div
          className="absolute inset-x-3 -bottom-2 h-10 rounded-[32px] blur-2xl opacity-80 bg-gradient-to-r from-[#8B5CF6] via-[#A78BFA] to-[#C4B5FD] group-hover:opacity-100 transition-opacity"
          aria-hidden
        />
        <div className="relative aspect-square rounded-2xl overflow-hidden bg-[#1A1D4A] border border-white/5 shadow-[0_10px_40px_-10px_rgba(139,92,246,0.6)] group-hover:shadow-[0_18px_60px_-10px_rgba(139,92,246,0.9)] transition-shadow">
          <img
            src={imgSrc}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
          {outOfStock && (
            <div className="absolute inset-0 bg-black/55 flex items-center justify-center">
              <span className="px-3 py-1.5 rounded-full text-[11px] font-bold uppercase tracking-wider bg-white text-[#0E0E23]">Sold out</span>
            </div>
          )}
          {!outOfStock && compareAt && (
            <span className="absolute top-3 left-3 px-2 py-0.5 rounded-md text-[10px] font-bold tracking-wider bg-[#8B5CF6] text-white">
              SALE
            </span>
          )}
        </div>
      </Link>

      <div className="mt-4 px-1">
        <Link to={`/shop/item/${item.item_id}`} className="block">
          <h3 className="font-syne text-base font-bold text-white leading-tight line-clamp-2 group-hover:text-[#C4B5FD] transition-colors" data-testid={`shop-item-title-${item.item_id}`}>
            {item.title}
          </h3>
        </Link>
        <div className="mt-1.5 flex items-baseline gap-2">
          <span className="text-white font-semibold">${price}</span>
          {compareAt && (
            <span className="text-[11px] text-[#9CA3AF] line-through">${compareAt}</span>
          )}
        </div>
        {item.vendor && (
          <Link
            to={`/vendor/${item.vendor.vendor_id}`}
            className="mt-1.5 flex items-center gap-1.5 text-xs text-[#9CA3AF] hover:text-[#C4B5FD] transition-colors"
            data-testid={`shop-item-vendor-${item.item_id}`}
          >
            <Store size={11} strokeWidth={1.5} />
            {item.vendor.business_name}
          </Link>
        )}
      </div>
    </div>
  );
}

export default function ShopPage() {
  useSeo({
    title: 'Shop · EntrepreNatives',
    description: 'Browse handcrafted items from Indigenous-owned businesses across the Americas. One-of-a-kind creations, art, beadwork, apparel, and more.',
    url: 'https://entreprenatives.com/shop',
  });

  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [cat, setCat] = useState('');
  const [sort, setSort] = useState('newest');
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [hasMore, setHasMore] = useState(false);

  // Pull categories once (only categories that have at least one active item).
  useEffect(() => {
    fetch(`${API}/public/items/categories`)
      .then((r) => r.json())
      .then((data) => setCategories(Array.isArray(data) ? data : []))
      .catch(() => setCategories([]));
  }, []);

  // Fetch items whenever filters change.
  useEffect(() => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim()) params.set('q', q.trim());
    if (cat) params.set('category_id', cat);
    params.set('sort', sort);
    params.set('page', String(page));
    params.set('limit', '24');
    fetch(`${API}/public/items/browse?${params}`)
      .then((r) => r.json())
      .then((data) => {
        setItems((prev) => (page === 1 ? data.items : [...prev, ...data.items]));
        setTotal(data.total || 0);
        setHasMore(!!data.has_more);
      })
      .finally(() => setLoading(false));
  }, [q, cat, sort, page]);

  // When filters change, reset page.
  useEffect(() => { setPage(1); }, [q, cat, sort]);

  const headerCount = useMemo(() => {
    if (loading && items.length === 0) return 'Loading…';
    if (total === 0) return 'No items yet';
    return `${total.toLocaleString()} items for sale`;
  }, [loading, items.length, total]);

  return (
    <div className="min-h-screen bg-[#0A0A23] text-white">
      <Navbar />

      <main className="pt-24 pb-20">
        <div className="container-main">
          {/* ─── Header ────────────────────────────────────────────── */}
          <div className="mb-8 flex items-end justify-between gap-6 flex-wrap">
            <div>
              <h1 className="font-syne text-4xl sm:text-5xl font-bold tracking-tight">
                Shop <span className="text-[#C4B5FD]">the Marketplace</span>
              </h1>
              <p className="mt-3 text-[#9CA3AF] text-sm sm:text-base max-w-2xl">
                Handcrafted finds from Indigenous-owned businesses across the Americas.
                100% of the sale goes to the artisan — zero platform fees.
              </p>
              <p className="mt-2 text-xs text-[#9CA3AF]/70 uppercase tracking-wider" data-testid="shop-count">
                {headerCount}
              </p>
            </div>

            <div className="flex items-center gap-3 flex-wrap">
              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#9CA3AF]" />
                <input
                  type="text"
                  placeholder="Search items…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-10 pr-4 h-10 w-64 rounded-full bg-white/5 border border-white/10 text-sm text-white placeholder:text-[#9CA3AF] focus:outline-none focus:border-[#8B5CF6]"
                  data-testid="shop-search-input"
                />
              </div>
              {/* Sort */}
              <select
                value={sort}
                onChange={(e) => setSort(e.target.value)}
                className="h-10 rounded-full bg-white/5 border border-white/10 px-4 text-sm text-white focus:outline-none focus:border-[#8B5CF6]"
                data-testid="shop-sort-select"
              >
                <option value="newest">Newest</option>
                <option value="popular">Most popular</option>
                <option value="price_low">Price: low → high</option>
                <option value="price_high">Price: high → low</option>
              </select>
            </div>
          </div>

          {/* ─── Categories ─────────────────────────────────────────── */}
          <div className="mb-10 overflow-x-auto">
            <div className="flex items-center gap-2 pb-2" data-testid="shop-categories">
              <button
                onClick={() => setCat('')}
                data-testid="shop-category-all"
                className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${!cat ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white' : 'bg-white/5 border-white/10 text-[#C4B5FD] hover:bg-white/10'}`}
              >
                All categories
              </button>
              {categories.map((c) => (
                <button
                  key={c.id}
                  onClick={() => setCat(c.id)}
                  data-testid={`shop-category-${c.id}`}
                  className={`px-4 py-2 rounded-full text-xs font-semibold whitespace-nowrap border transition-all ${cat === c.id ? 'bg-[#8B5CF6] border-[#8B5CF6] text-white' : 'bg-white/5 border-white/10 text-[#C4B5FD] hover:bg-white/10'}`}
                >
                  {c.name} · {c.count}
                </button>
              ))}
            </div>
          </div>

          {/* ─── Grid ───────────────────────────────────────────────── */}
          {loading && items.length === 0 ? (
            <div className="flex items-center justify-center py-24">
              <Loader2 className="w-6 h-6 animate-spin text-[#8B5CF6]" />
            </div>
          ) : items.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-white/10 py-20 text-center text-[#9CA3AF]" data-testid="shop-empty">
              <p className="text-lg font-semibold text-white">No items match your filters</p>
              <p className="mt-2 text-sm">Try clearing search or picking a different category.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-x-6 gap-y-12" data-testid="shop-grid">
                {items.map((it) => (
                  <ItemCard key={it.item_id} item={it} />
                ))}
              </div>

              {hasMore && (
                <div className="mt-12 flex justify-center">
                  <button
                    onClick={() => setPage((p) => p + 1)}
                    disabled={loading}
                    className="px-6 py-3 rounded-full text-sm font-semibold bg-white/5 border border-white/10 text-[#C4B5FD] hover:bg-white/10 disabled:opacity-50"
                    data-testid="shop-load-more"
                  >
                    {loading ? 'Loading…' : 'Show more'}
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}
